import type { ApiResponse } from "./schema";

/**
 * Utility class used to simplify making requests to the Sawdays API
 *
 * The expected return format for both the '/places' and '/bookables' endpoints
 * can be found in the 'schema.ts' file.
 *
 * @author Atomic Smash <developers@atomicsmash.co.uk>
 * @version 1.0.0
 */
class Api
{
    base_url: string;

    /**
     * Create an API adapter around the provided `base_url`
     *
     * @param base_url Base URL for all relative API endpoints
     */
    constructor(base_url: string) {
        this.base_url = base_url;
    }

    /**
     * Query place availability between two dates
     *
     * @param place_id   Place ID
     * @param guests     Expected guests
     * @param start_date Start date (YYYY-MM-DD)
     * @param end_date   End date (YYYY-MM-DD)
     * @return           Promise resolving to API response
     */
    queryPlace(place_id: string, guests: number, start_date: string, end_date: string): Promise<ApiResponse> {
        return this.makeRequest<ApiResponse>(
            `places/${place_id}/availability/${start_date}/${end_date}/${guests}`
        );
    }

    /**
     * Query bookable availability between two dates
     *
     * @param bookable_id Bookable ID
     * @param start_date  Start date (YYYY-MM-DD)
     * @param end_date    End date (YYYY-MM-DD)
     * @return            Promise resolving to API response
     */
    queryBookable(bookable_id: string, start_date: string, end_date: string): Promise<ApiResponse> {
        return this.makeRequest<ApiResponse>(
            `bookables/${bookable_id}/availability/${start_date}/${end_date}`
        );
    }

    /**
     * Perform a HTTP request to the given endpoint
     *
     * @private
     * @param endpoint API endpoint
     * @return         Promise resolving to JSON response
     */
    makeRequest<T>(endpoint: string): Promise<T> {
        const url = this.base_url + endpoint;

        return fetch(url, {
            method: "GET",
            headers: {
                "Accept": "application/json"
            }
        }).then((response) => {
            if (response.status !== 200)
                throw new Error(`API failed with HTTP ${response.status}`);

            return response.json();
        });
    }
}

export default Api;
